import { Service } from "models/Service";
import { getPrevDateNotWeekend, parseDataCurrencies, parseDataBonds } from "helpers";

const pagegrowlerFrontendApiUrl = process.env.PAGEGROWLER_FRONTEND_API ?? process.env.NEXT_PUBLIC_PAGEGROWLER_FRONTEND_API;
const mercadosApiUrl = `${process.env.VARNISH_API_MERCADOS_URL}/api/mercados`;

// ids de IOL (/iol/exchange-rates) -> códigos usados en models/CurrencyBond/data.js
const IOL_CURRENCY_IDS = {
  205: "DBNA",
  15: "EURPES",
  3: "CMPES",
  22: "URUPES",
  10: "LIBPES",
};

// número -> string con formato argentino ("1545,5" / "3.047.717,20")
const toAr = (value, { grouping = false, decimals = 2, maxDecimals = decimals } = {}) =>
  typeof value === "number"
    ? value.toLocaleString("es-AR", {
        useGrouping: grouping,
        minimumFractionDigits: decimals,
        maximumFractionDigits: maxDecimals,
      })
    : "";

// "2026-09-29T12:54:00-03:00" -> { data: "29/09/2026", hora: "12:54" } (hora local de la fuente)
const splitTradeTime = (tradeTime) => {
  const [date = "", time = ""] = (tradeTime ?? "").split("T");
  const [year, month, day] = date.split("-");
  return {
    data: year ? `${day}/${month}/${year}` : "",
    hora: time.slice(0, 5),
  };
};

export class CurrencyBond extends Service {
  constructor() {
    super({
      adsPath: "servicios/divisas",
      meta: {
        description: `Los principales indicadores económicos de Argentina y el mundo. Noticias del dólar, Merval, empresas argentinas, acciones y bonos de deuda.`,
        keywords: "bonos, bolsa, moneda, dolar, euro, dolar blue",
        title: `Divisas, Acciones y Bonos - Clarin.com`,
        url: "economia/divisas-acciones-bonos",
        extra: [],
      },
      section: "economia",
      sectionAds: "divisas",
      template: "CurrenciesBonds",
      title: `Dólar blue, dólar hoy, euro, real, cripto, divisas, acciones y bonos`,
      titleHeader: "Dólar, euro, real, acciones y bonos hoy",
      slug: "dolar",
      breadcrumb: [
        {
          title: "Divisas, Acciones y Bonos",
          url: "/economia/divisas-acciones-bonos",
        }
      ],
      showTitleHeaderApp: false,
      showMenuApp: false,
      showBreadcrumbApp: false,
      showBannerH1App: false,
    });
  }

  getBond = async () => {
    const { data } = await this.fetchData(
      `${mercadosApiUrl}/bonos`
    );
    return data?.items ?? [];
  };

  getCurrency = async () => {
    const { data } = await this.fetchData(
      `${mercadosApiUrl}/iol/exchange-rates`
    );
    const items = data?.item?.items ?? [];
    const fallbackTime = data?.item?.generated_at;
    return items
      .filter((item) => IOL_CURRENCY_IDS[item.id])
      .map((item) => {
        const { data: date, hora } = splitTradeTime(item.trade_time ?? fallbackTime);
        return {
          papel: IOL_CURRENCY_IDS[item.id],
          descricao: item.name,
          ultimo: toAr(item.ask, { maxDecimals: 4 }),
          compra: toAr(item.bid, { maxDecimals: 4 }),
          variacao: toAr(item.variation),
          fechant: toAr(item.previous_close, { maxDecimals: 4 }),
          data: date,
          hora,
        };
      });
  };

  getMervalPanel = async () => {
    const { data } = await this.fetchData(
      `${mercadosApiUrl}/paneles`
    );
    return data?.items ?? [];
  };

  getMervalValue = async () => {
    const { data } = await this.fetchData(
      `${mercadosApiUrl}/iol/indexes`
    );
    const items = data?.item?.items ?? [];
    return items
      // IOL devuelve algunos índices sin cotización (value 0 / trade_time null)
      .filter((item) => item.trade_time && item.value)
      .map((item) => {
        const { data: date, hora } = splitTradeTime(item.trade_time);
        return {
          papel: String(item.id),
          descricao: item.name,
          ultimo: toAr(item.value, { grouping: true }),
          variacao: `${toAr(item.variation)}%`,
          fechant: toAr(item.previous_close, { grouping: true }),
          data: date,
          hora,
        };
      });
  };

  getUsdBlueTourist = async () => {
    const { data } = await this.fetchData(
      `${mercadosApiUrl}/redaccion/latest`
    );
    let items = data?.items ?? [];
    if (items?.[0]?.createdAt) {
      const prevDate = getPrevDateNotWeekend(items[0].createdAt);
      let dataPrevDate = await this.fetchData(
        `${mercadosApiUrl}/redaccion/all?date=${prevDate.replace(/\//g, "-")}`
      );
      dataPrevDate = dataPrevDate?.data?.items?.length
        ? this.formatBlueTouristPrev(dataPrevDate.data.items[dataPrevDate.data.items.length - 1])
        : [];
      items[0].prevDate = dataPrevDate;
    }
    return items;
  };

  formatBlueTouristPrev = (blueTourist) => {
    return {
      blue_compra: blueTourist?.data?.blue_compra ?? "0",
      blue_venta: blueTourist?.data?.blue_venta ?? "0",
      dolar_turista: blueTourist?.data?.dolar_turista ?? "0",
      bolsa_compra: blueTourist?.data?.bolsa_compra ?? "0",
      bolsa_venta: blueTourist?.data?.bolsa_venta ?? "0",
      contado_con_liqui_compra: blueTourist?.data?.contado_con_liqui_compra ?? "0",
      contado_con_liqui_venta: blueTourist?.data?.contado_con_liqui_venta ?? "0",
      dolar_bullexchange_compra: blueTourist?.data?.dolar_bullexchange_compra ?? "0",
      dolar_bullexchange_venta: blueTourist?.data?.dolar_bullexchange_venta ?? "0",
    };
  };

  parseData = async (query) => {
    let dataFront = null;
    const slug = this.getSlug();
    if (query?.[3]) {
      this.setMeta(this.parseMeta({  
        url: `economia/${query?.[1]}/${query?.[2]}/${query?.[3]}`, 
      }))
    }
    if (query?.[1] === "calculadora-ganancias") {

      this.setTemplate("IncomeTaxCalculator");
      this.setMeta(this.parseMeta({
        description: `Calculadora de ganancias - Clarín.com`,
        keywords: "calculadora, ganancias",
        title: `Calculadora de ganancias`,
        url: `economia/calculadora-ganancias`,
        extra: []
      }));
      this.setTitle('Calculadora de ganancias');
      this.setSlug('impuesto-a-las-ganancias');
      this.setSectionAds("calculadora-ganancias");
      this.setBreadcrumb([
        {
          title: "Economía",
          url: "/economia",
        },
        {
          title: "Calculadora de ganancias",
          url: "",
        }
      ])

    } else if (query?.[1] === "calculadora-inflacion") {

      this.setTemplate("PaymentCalculation");
      this.setMeta(this.parseMeta({
        description: `Calculadora de inflación en Argentina: consultá si te conviene pagar de contado o en cuotas - Clarín.com`,
        keywords: "calculadora, inflacion",
        title: `Calculadora de inflación en Argentina: consultá si te conviene pagar de contado o en cuotas`,
        url: `economia/calculadora-inflacion`,
        extra: []
      }));
      this.setTitle('Calculadora de inflación en Argentina: consultá si te conviene pagar de contado o en cuotas');
      this.setSectionAds("calculadora-inflacion");
      this.setBreadcrumb([
        {
          title: "Economía",
          url: "/economia",
        },
        {
          title: "Calculadora de inflación",
          url: "",
        }
      ])

    } else if (query?.[1] === "calculadora-aguinaldo-cuanto-dinero-extra-vas-recibir") {

      dataFront = {
        ...dataFront,
        bonus_calculator: {
          description: 'Calculadora de aguinaldo y aguinaldo proporcional a los meses trabajados para empleados de comercio en Argentina, con cálculos de montos netos y brutos.',
          keywords: "calculadora, aguinaldo",
          short_title: 'Calculadora de aguinaldo',
          slug: query?.[1],
          text: '¿Tenés dudas acerca de cuánto dinero vas a recibir? Nuestra calculadora de aguinaldo te da un cálculo confiable. ¡Simplificá tu vida financiera y tomá el control de tus finanzas!',
          title: 'Calculadora de Aguinaldo (Sueldo Anual Complementario) Online, y SAC proporcional',
          iframe: '//calculadora-aguinaldo.agea.com.ar/'
        }
      }

      this.setTemplate("BonusCalculator");
      this.setMeta(this.parseMeta({
        description: `${dataFront.bonus_calculator.description} - Clarín.com`,
        keywords: `${dataFront.bonus_calculator.keywords}`,
        title: `${dataFront.bonus_calculator.title}`,
        url: `economia/${dataFront.bonus_calculator.slug}`,
        extra: []
      }));
      this.setTitle(`${dataFront.bonus_calculator.title}`);
      this.setSectionAds(`${dataFront.bonus_calculator.slug}`);
      this.setBreadcrumb([
        {
          title: "Economía",
          url: "/economia",
        },
        {
          title: `${dataFront.bonus_calculator.short_title}`,
          url: "",
        }
      ])
    } else if (query?.[1] === "calculadora-monotributo") {
      this.setTemplate("SingleTaxCalculator");
      this.setMeta(this.parseMeta({
        description: `Calculadora monotributo - Clarín.com`,
        keywords: "calculadora, monotributo",
        title: `Calculadora monotributo`,
        url: `economia/calculadora-monotributo`,
        extra: []
      }));
      this.setTitle('Calculadora monotributo');
      this.setSlug('monotributo');
      this.setSectionAds("calculadora-monotributo");
      this.setBreadcrumb([
        {
          title: "Economía",
          url: "/economia",
        },
        {
          title: "Calculadora monotributo",
          url: "",
        }
      ])
    } else if (query?.[1] === "calculadora-alquileres") {
      // legacy URL: /economia/calculadora-alquileres → /propiedades/calculadora-alquileres
      this.setData({ redirect: "/propiedades/calculadora-alquileres" });
      return;
    } else {

      const [bonds, currency, mervalPanel, mervalValue, usdBlueTourist] = await Promise.all([
        this.getBond(),
        this.getCurrency(),
        this.getMervalPanel(),
        this.getMervalValue(),
        this.getUsdBlueTourist(),
      ]);
      dataFront = { bonds, currency, mervalPanel, mervalValue, usdBlueTourist };

      if (query?.[1] === "divisas-acciones-bonos" && !query?.[2]) {
        this.setIdBucket("discover-lx");
      }
    }

    //const notes = await this.fetchData(`${process.env.LILAX_FRONTEND_API}latest/?type=lilanews&tag=${slug}&limit=6`);
    const notes = await this.fetchData(`${pagegrowlerFrontendApiUrl}lilanews/?type=listType&tag=${slug}&limit=6`);
    this.setData({
      ...dataFront,
      notes: notes?.data?.content ?? [],
      coin: "",
      bond: ""
    });
    if (query[2] === "monedas") {
      this.setTitleHeaderLink("/economia/divisas-acciones-bonos");
      this.setTitleHeaderTag("p");      
      const sectionAds = query[3] && query[3].replace("-", "")
      this.setSectionAds(sectionAds)
      const { primary } = parseDataCurrencies(dataFront.currency, dataFront.usdBlueTourist);
      const coin = primary.filter(
        (value) => {
          if (value?.url == query[3]) {
            return value;
          };
        }
      );
      if (coin[0]) {
        this.setTemplate("Currencies");
        this.setBreadcrumb([
          {
            title: "Divisas, Acciones y Bonos",
            url: "/economia/divisas-acciones-bonos",
          },
          {
            title: coin[0].description,
            url: "",
          }
        ])
        this.setData({
          ...dataFront,
          notes: notes?.data?.content ?? [],
          coin: coin[0] ? coin[0] : "",
          bond: ""
        });
        this.setTitle(`${coin[0].name} hoy, cotización y precio del ${coin[0].name} | Clarín`);
        this.setMeta(this.parseMeta({
          keywords: "Dólar, Bonos, Bolsa, Moneda, Divisas, bonos, Dólar oficial, Dólar blue, Dólar turista, Dólar MEP, Dólar CCL, Calculadora de Ganancias, Calculadora de inflación",
          description: `${coin[0].name}. Los principales indicadores económicos de Argentina y el mundo. Noticias del dólar, Merval, empresas argentinas, acciones y bonos de deuda.`,
          url: `economia/${query?.[1]}/${query?.[2]}/${query?.[3]}`, 
        }));
      }
    }

    if (query[2] === "bonos") {
      this.setTitleHeaderLink("/economia/divisas-acciones-bonos");
      this.setTitleHeaderTag("p");
      this.setTitle(`${query[3]}, divisas, acciones y bonos`);
      this.setMeta(this.parseMeta({
        keywords: "Dólar, Bonos, Bolsa, Moneda, Divisas, bonos, Dólar oficial, Dólar blue, Dólar turista, Dólar MEP, Dólar CCL, TVPY, Calculadora de Ganancias, Calculadora de inflación",
        description: `${query[3]}. Los principales indicadores económicos de Argentina y el mundo. Noticias del dólar, Merval, empresas argentinas, acciones y bonos de deuda.`,
        url: `economia/${query?.[1]}/${query?.[2]}/${query?.[3]}`, 
      }));
      const sectionAds = query[3] && query[3].replace("-", "")
      this.setSectionAds(sectionAds)
      const bondsData = parseDataBonds(dataFront.bonds);
      const bond = bondsData.filter(
        (value) => {
          if (value?.papel == query[3]) {
            return value;
          };
        }
      );
      if (bond[0]) {
        this.setTemplate("Bonds");
        this.setBreadcrumb([
          {
            title: "Divisas, Acciones y Bonos",
            url: "/economia/divisas-acciones-bonos",
          },
          {
            title: "Bonos",
            url: "/economia/divisas-acciones-bonos",
          },
          {
            title: bond[0].papel,
            url: "",
          }
        ])
        this.setData({
          ...dataFront,
          notes: notes?.data?.content ?? [],
          coin: "",
          bond: bond[0]
        });
      }
    }
  };
}
